import { useEffect, useState } from 'react'
import { theme } from '../theme'
import { Ic, RecipeThumb } from '../icons'
import { api } from '../api'
import type { RecipeDetail, RecipeIngredient, IngredientRow } from '../types'
import { IngredientPicker } from './IngredientPicker'
import { IngredientCreateModal } from './IngredientCreateModal'
import { IngredientEditModal } from './IngredientEditModal'
import { categoryOf, unitsForCategory, groupUnitsForPicker } from '../units'
import { SLOT_KEYS, SLOT_LABELS } from '../dates'
import {
  DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors,
  closestCenter, type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext, useSortable, arrayMove,
  sortableKeyboardCoordinates, verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

// Local row type: ingredients carry a synthetic _uid so dnd-kit has a stable
// id that survives reordering (ingredient_id is 0 for fresh rows and may
// collide while the user is editing).
type IngRow = RecipeIngredient & { _uid: number }
let __uidCounter = 1
const nextUid = () => __uidCounter++

type Props = {
  recipeId: number
  mobile: boolean
  onDone: () => void
  onCancel: () => void
}

type TagPair = { key: string; value: string }

export function RecipeEditPage({ recipeId, mobile, onDone, onCancel }: Props) {
  const [detail, setDetail] = useState<RecipeDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const [name, setName] = useState('')
  const [prep, setPrep] = useState(0)
  // Unified yield: a quantity in yieldUnit. 'serving' yields behave like the old
  // servings field; mass/volume/count yields are for measured-ingredient recipes.
  const [yieldQty, setYieldQty] = useState(0)
  const [yieldUnit, setYieldUnit] = useState('serving')
  const [instructions, setInstructions] = useState('')
  const [ingredients, setIngredients] = useState<IngRow[]>([])
  const [tags, setTags] = useState<TagPair[]>([])
  const [slots, setSlots] = useState<string[]>([])
  // used_as: how the recipe can be used — 'meal' (eaten/scheduled) and/or
  // 'ingredient' (a component in other recipes). Absent on the wire = ['meal'].
  const [usedAs, setUsedAs] = useState<string[]>(['meal'])
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined)
  const [photoBusy, setPhotoBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    api.recipe(recipeId)
      .then(d => {
        if (cancelled) return
        if (!d.mine) {
          setError('Only recipes in your cookbook can be edited. Adopt this recipe first.')
          setDetail(d)
          return
        }
        setDetail(d)
        setName(d.name)
        setPrep(d.prep_minutes)
        setYieldQty(d.yield_quantity || d.servings || 0)
        setYieldUnit(d.yield_unit || 'serving')
        setInstructions(d.instructions)
        setIngredients(d.ingredients.map(i => ({ ...i, _uid: nextUid() })))
        // `slot` and `used_as` have dedicated controls; filter them out of the
        // free-form custom-tags editor.
        setTags(
          Object.entries(d.tags)
            .filter(([k]) => k !== 'slot' && k !== 'used_as')
            .flatMap(([k, vs]) => vs.map(v => ({ key: k, value: v })))
        )
        setSlots((d.tags.slot ?? []).filter(v => (SLOT_KEYS as readonly string[]).includes(v)))
        // Absent used_as means the recipe is a meal by default.
        setUsedAs(d.tags.used_as && d.tags.used_as.length ? d.tags.used_as : ['meal'])
        setImageUrl(d.image_url)
      })
      .catch(e => { if (!cancelled) setError(String(e)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [recipeId])

  const [viewingIng, setViewingIng] = useState<IngredientRow | null>(null)
  const [viewKey, setViewKey] = useState(0)

  // Open the Edit ingredient modal for a recipe row's selected ingredient.
  // Fetches the canonical row by id and forks system rows so the editor opens
  // editable; the recipe row is retargeted to the user's fork so subsequent
  // saves reference the (now editable) ingredient.
  const openIngredientView = async (rowIdx: number, ingredientID: number) => {
    setError(null)
    try {
      let row = await api.getIngredient(ingredientID)
      if (!row.owned) {
        row = await api.adoptIngredient(row.id)
        setIng(rowIdx, { ingredient_id: row.id })
      }
      setViewingIng(row)
      setViewKey(k => k + 1)
    } catch (e) {
      setError(String(e))
    }
  }

  const [createOpen, setCreateOpen] = useState<{ rowIdx: number; seed: string } | null>(null)
  // Track the last *opened* modal key so closing doesn't flip the key
  // (which would unmount mid-fade and skip the close transition).
  const [createModalKey, setCreateModalKey] = useState<string>('init')
  useEffect(() => {
    if (createOpen) setCreateModalKey(`${createOpen.rowIdx}:${createOpen.seed}`)
  }, [createOpen])

  const setIng = (idx: number, patch: Partial<RecipeIngredient>) =>
    setIngredients(arr => arr.map((it, i) => i === idx ? { ...it, ...patch } : it))
  const removeIng = (idx: number) => setIngredients(arr => arr.filter((_, i) => i !== idx))

  // New ingredients are inserted at the *top* and animated in: the freshly
  // added card expands from zero height (grid 0fr→1fr), which pushes every
  // existing row downward so it's obvious a slot appeared. `entering` tracks
  // the new row's _uid through its collapse→expand→settle lifecycle; once the
  // animation finishes we drop the wrapper so it doesn't clip the ingredient
  // picker's absolutely-positioned dropdown.
  const [entering, setEntering] = useState<{ uid: number; expanded: boolean } | null>(null)
  const addIng = () => {
    const uid = nextUid()
    setIngredients(arr => [{ _uid: uid, ingredient_id: 0, name: '', default_unit: '', quantity: 0, unit: '', optional: false, prep: '' }, ...arr])
    setEntering({ uid, expanded: false })
  }
  // Two-phase mount: paint the collapsed state, then (double rAF) flip to
  // expanded so the browser actually runs the transition.
  useEffect(() => {
    if (!entering || entering.expanded) return
    let id2 = 0
    const id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => setEntering(e => e ? { ...e, expanded: true } : e))
    })
    return () => { cancelAnimationFrame(id1); cancelAnimationFrame(id2) }
  }, [entering])
  // Drop the animation wrapper once the expand transition has finished.
  useEffect(() => {
    if (!entering || !entering.expanded) return
    const t = window.setTimeout(() => setEntering(null), 460)
    return () => window.clearTimeout(t)
  }, [entering])

  // Pointer sensor with a small distance threshold so a click on an input
  // inside a card doesn't accidentally start a drag — the user has to move
  // 4px after pressing before dnd-kit treats it as a drag. The handle
  // attaches the listeners, so this only matters as a safety net.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e
    if (!over || active.id === over.id) return
    setIngredients(arr => {
      const from = arr.findIndex(r => r._uid === active.id)
      const to = arr.findIndex(r => r._uid === over.id)
      if (from < 0 || to < 0) return arr
      return arrayMove(arr, from, to)
    })
  }

  const pickIngredient = (idx: number, row: IngredientRow) => {
    if (row.component_recipe_id) {
      // A recipe picked as a sub-recipe component: it's measured in the
      // sub-recipe's yield unit (row.default_unit carries it).
      const cu = row.default_unit || 'serving'
      setIng(idx, {
        ingredient_id: 0,
        component_recipe_id: row.component_recipe_id,
        name: row.name,
        default_unit: cu,
        unit: cu,
      })
      return
    }
    setIng(idx, {
      ingredient_id: row.id,
      component_recipe_id: 0,
      name: row.name,
      default_unit: row.default_unit,
      unit: row.default_unit || '',
    })
  }

  const setTag = (idx: number, patch: Partial<TagPair>) =>
    setTags(arr => arr.map((it, i) => i === idx ? { ...it, ...patch } : it))
  const removeTag = (idx: number) => setTags(arr => arr.filter((_, i) => i !== idx))
  const addTag = () => setTags(arr => [...arr, { key: '', value: '' }])

  const toggleSlot = (s: string) =>
    setSlots(arr => arr.includes(s) ? arr.filter(x => x !== s) : [...arr, s])

  // A recipe usable as a meal (used_as includes 'meal', or nothing is chosen
  // → default meal) is eaten in servings, so its yield must be in 'serving'.
  // Only an ingredient-only recipe may yield in mass/volume/count units.
  const isMeal = usedAs.length === 0 || usedAs.includes('meal')
  const toggleUsedAs = (v: string) =>
    setUsedAs(arr => arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v])
  // Snap the yield unit back to servings whenever the recipe is (still) a meal.
  useEffect(() => {
    if (isMeal && yieldUnit !== 'serving') setYieldUnit('serving')
  }, [isMeal, yieldUnit])

  // Photo upload is its own immediate write (separate endpoint), independent of
  // the Save button. We downscale in a canvas first as a courtesy — the server
  // re-encodes regardless and is the source of truth.
  const onPhotoFile = async (file: File | undefined) => {
    if (!file) return
    setPhotoBusy(true)
    setError(null)
    try {
      const base64 = await fileToResizedJpeg(file)
      const updated = await api.setRecipeImage(recipeId, base64, 'image/jpeg')
      setImageUrl(updated.image_url)
    } catch (e) {
      setError(String(e))
    } finally {
      setPhotoBusy(false)
    }
  }
  const removePhoto = async () => {
    setPhotoBusy(true)
    setError(null)
    try {
      await api.deleteRecipeImage(recipeId)
      setImageUrl(undefined)
    } catch (e) {
      setError(String(e))
    } finally {
      setPhotoBusy(false)
    }
  }

  const save = async () => {
    if (!name.trim()) { setError('Name is required.'); return }
    if (usedAs.length === 0) { setError('Pick how this recipe is used (meal and/or ingredient).'); return }
    if (!(Number(yieldQty) > 0)) { setError('Enter a yield greater than 0.'); return }
    // A meal recipe needs at least one slot; an ingredient-only recipe doesn't.
    if (isMeal && slots.length === 0) { setError('Pick at least one slot.'); return }
    setSaving(true)
    setError(null)
    try {
      const tagMap: Record<string, string[]> = {}
      for (const { key, value } of tags) {
        const k = key.trim(); const v = value.trim()
        if (!k || k === 'slot' || k === 'used_as' || !v) continue
        if (!tagMap[k]) tagMap[k] = []
        if (!tagMap[k].includes(v)) tagMap[k].push(v)
      }
      if (slots.length > 0) tagMap.slot = [...slots]
      tagMap.used_as = [...usedAs]
      // A row is real if it resolved to an ingredient OR a sub-recipe component.
      const cleanIngs = ingredients.filter(i => i.ingredient_id > 0 || (i.component_recipe_id ?? 0) > 0)
      for (const i of cleanIngs) {
        if (!i.unit) { setError(`Pick a unit for ${i.name}.`); setSaving(false); return }
      }
      await api.updateRecipe(recipeId, {
        name: name.trim(),
        prep_minutes: Number(prep) || 0,
        servings: yieldUnit === 'serving' ? Math.round(Number(yieldQty)) : 0,
        yield_quantity: Number(yieldQty),
        yield_unit: yieldUnit,
        instructions,
        ingredients: cleanIngs.map(i => ({
          ingredient_id: i.ingredient_id,
          component_recipe_id: (i.component_recipe_id ?? 0) > 0 ? i.component_recipe_id : undefined,
          quantity: i.quantity,
          unit: i.unit,
          optional: i.optional,
          prep: i.prep?.trim() || undefined,
        })),
        tags: tagMap,
      })
      onDone()
    } catch (e) {
      setError(String(e))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div style={{ fontFamily: theme.sans, fontSize: 13, color: theme.muted }}>Loading…</div>
  }

  if (error && !detail) {
    return (
      <Page>
        <Header title="Edit recipe" onCancel={onCancel} onSave={save} saving={saving} disabled/>
        <div style={{
          padding: 16, border: `1px solid ${theme.lineStrong}`, borderRadius: theme.radius,
          color: theme.accentInk, fontFamily: theme.sans, fontSize: 13,
        }}>{error}</div>
      </Page>
    )
  }

  if (detail && !detail.mine) {
    return (
      <Page>
        <Header title="Edit recipe" onCancel={onCancel} onSave={save} saving={saving} disabled/>
        <div style={{
          padding: 16, border: `1px solid ${theme.lineStrong}`, borderRadius: theme.radius,
          color: theme.inkSoft, fontFamily: theme.sans, fontSize: 13,
        }}>
          This recipe is in the catalog. Adopt it to your cookbook before editing.
        </div>
      </Page>
    )
  }

  return (
    <Page>
      <Header title={detail?.name || 'Edit recipe'} onCancel={onCancel} onSave={save} saving={saving}
        disabled={!name.trim() || usedAs.length === 0 || !(Number(yieldQty) > 0) || (isMeal && slots.length === 0)}/>

      {error && (
        <div style={{
          padding: '8px 12px', borderRadius: theme.radius,
          background: 'rgba(184,83,57,0.08)', color: theme.accentInk,
          fontFamily: theme.sans, fontSize: 12,
        }}>{error}</div>
      )}

      <Section label="Basics">
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: mobile ? '1fr' : '1fr 120px' }}>
          <Field label="Name">
            <input value={name} onChange={e => setName(e.target.value)} style={inputStyle}/>
          </Field>
          <Field label="Prep (min)">
            <input type="number" min={0} value={prep} onChange={e => setPrep(Number(e.target.value))} style={inputStyle}/>
          </Field>
        </div>
        <div style={{ marginTop: 12 }}>
          <Field label="Yield">
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
              <input type="number" min={0} step="any" value={yieldQty || ''}
                onChange={e => setYieldQty(Number(e.target.value) || 0)} style={inputStyle}/>
              <select value={yieldUnit} onChange={e => setYieldUnit(e.target.value)}
                style={{ ...inputStyle, appearance: 'auto' }}>
                <option value="serving">servings</option>
                {!isMeal && (() => {
                  const g = groupUnitsForPicker()
                  return (['mass', 'volume', 'count'] as const).map(cat => (
                    <optgroup key={cat} label={cat}>
                      {g[cat].map(u => <option key={u.code} value={u.code}>{u.label}</option>)}
                    </optgroup>
                  ))
                })()}
              </select>
            </div>
            <div style={{ fontFamily: theme.sans, fontSize: 11, color: theme.muted, marginTop: 4 }}>
              {isMeal
                ? 'Meals are measured in servings. Make this recipe ingredient-only (below) to yield in g/ml/etc.'
                : 'How much the full recipe makes — used when it’s an ingredient in other recipes.'}
            </div>
          </Field>
        </div>
      </Section>

      <Section label="Photo">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <RecipeThumb imageUrl={imageUrl} hue={detail?.hue ?? 30} name={name || '?'} size={72} radius={theme.radius}/>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <label style={{
                ...pillBtn,
                color: theme.surface, background: theme.ink, border: `1px solid ${theme.ink}`,
                cursor: photoBusy ? 'not-allowed' : 'pointer', opacity: photoBusy ? 0.5 : 1,
              }}>
                {imageUrl ? 'Replace' : 'Upload photo'}
                <input type="file" accept="image/*" disabled={photoBusy} style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; onPhotoFile(f) }}/>
              </label>
              {imageUrl && (
                <button type="button" onClick={removePhoto} disabled={photoBusy} style={{
                  ...pillBtn, color: theme.ink, border: `1px solid ${theme.lineStrong}`,
                  cursor: photoBusy ? 'not-allowed' : 'pointer', opacity: photoBusy ? 0.5 : 1,
                }}>Remove</button>
              )}
            </div>
            <div style={{ fontFamily: theme.sans, fontSize: 11, color: theme.muted }}>
              {photoBusy ? 'Saving…' : 'JPG, PNG or WebP. Large images are scaled down automatically.'}
            </div>
          </div>
        </div>
      </Section>

      <Section
        label="Ingredients"
        action={<AddBtn onClick={addIng} label="Add ingredient"/>}
      >
        {ingredients.length === 0 && <Empty>No ingredients yet.</Empty>}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={ingredients.map(r => r._uid)} strategy={verticalListSortingStrategy}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {ingredients.map((ing, i) => {
                const card = (
                  <SortableIngredientCard
                    ing={ing}
                    mobile={mobile}
                    onPick={row => pickIngredient(i, row)}
                    onCreateRequest={seed => setCreateOpen({ rowIdx: i, seed })}
                    onView={() => openIngredientView(i, ing.ingredient_id)}
                    onRemove={() => removeIng(i)}
                    onChange={patch => setIng(i, patch)}
                  />
                )
                const isEntering = entering?.uid === ing._uid
                // Same wrapper nesting in both branches so clearing `entering`
                // restyles in place rather than remounting the card (which
                // would reset the picker / drop focus).
                return (
                  <div key={ing._uid} style={isEntering ? {
                    display: 'grid',
                    gridTemplateRows: entering!.expanded ? '1fr' : '0fr',
                    opacity: entering!.expanded ? 1 : 0,
                    transition: 'grid-template-rows 380ms cubic-bezier(0.22, 1, 0.36, 1), opacity 320ms ease',
                  } : undefined}>
                    <div style={isEntering ? { overflow: 'hidden', minHeight: 0 } : undefined}>{card}</div>
                  </div>
                )
              })}
            </div>
          </SortableContext>
        </DndContext>
      </Section>

      <Section label="Instructions">
        <textarea value={instructions} onChange={e => setInstructions(e.target.value)}
          rows={mobile ? 8 : 12}
          style={{
            ...inputStyle,
            fontFamily: theme.serif, fontSize: 14, lineHeight: 1.55,
            resize: 'vertical', minHeight: 120,
          }}/>
      </Section>

      <IngredientCreateModal
        key={createModalKey}
        open={!!createOpen}
        seedName={createOpen?.seed ?? ''}
        onCancel={() => setCreateOpen(null)}
        onCreated={row => {
          if (createOpen) pickIngredient(createOpen.rowIdx, row)
          setCreateOpen(null)
        }}
      />

      <IngredientEditModal
        key={viewKey}
        open={viewingIng !== null}
        ingredient={viewingIng}
        onCancel={() => setViewingIng(null)}
        onSaved={() => setViewingIng(null)}
      />

      <Section label="Built-in tags">
        <Field label="Used as *">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {([['meal', 'Meal'], ['ingredient', 'Ingredient']] as const).map(([v, label]) => {
              const on = usedAs.includes(v)
              return (
                <button key={v} type="button" onClick={() => toggleUsedAs(v)} style={{
                  all: 'unset', cursor: 'pointer',
                  padding: '6px 12px', borderRadius: theme.radius,
                  fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  color: on ? theme.surface : theme.ink,
                  background: on ? theme.ink : 'transparent',
                  border: `1px solid ${on ? theme.ink : theme.lineStrong}`,
                }}>{label}</button>
              )
            })}
          </div>
          <div style={{ fontFamily: theme.sans, fontSize: 11, color: theme.muted, marginTop: 4 }}>
            <b>Meal</b>: eaten and scheduled on the plan. <b>Ingredient</b>: usable inside other recipes.
          </div>
          {usedAs.length === 0 && (
            <div style={{
              fontFamily: theme.sans, fontSize: 11, color: theme.accentInk,
              fontStyle: 'italic', marginTop: 4,
            }}>Pick at least one.</div>
          )}
        </Field>

        {isMeal && (
          <div style={{ marginTop: 12 }}>
            <Field label="Slot *">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {SLOT_KEYS.map(s => {
                  const on = slots.includes(s)
                  return (
                    <button key={s} type="button" onClick={() => toggleSlot(s)} style={{
                      all: 'unset', cursor: 'pointer',
                      padding: '6px 12px', borderRadius: theme.radius,
                      fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
                      textTransform: 'uppercase',
                      color: on ? theme.surface : theme.ink,
                      background: on ? theme.ink : 'transparent',
                      border: `1px solid ${on ? theme.ink : theme.lineStrong}`,
                    }}>{SLOT_LABELS[s]}</button>
                  )
                })}
              </div>
              {slots.length === 0 && (
                <div style={{
                  fontFamily: theme.sans, fontSize: 11, color: theme.accentInk,
                  fontStyle: 'italic', marginTop: 4,
                }}>Pick at least one slot.</div>
              )}
            </Field>
          </div>
        )}
      </Section>

      <Section label="Custom tags" action={<AddBtn onClick={addTag} label="Add tag"/>}>
        {tags.length === 0 && <Empty>No tags yet.</Empty>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {tags.map((t, i) => (
            <div key={i} style={{
              display: 'grid', gridTemplateColumns: '180px 1fr auto', gap: 6, alignItems: 'center',
            }}>
              <input placeholder="key" value={t.key}
                onChange={e => setTag(i, { key: e.target.value })} style={inputStyle}/>
              <input placeholder="value" value={t.value}
                onChange={e => setTag(i, { value: e.target.value })} style={inputStyle}/>
              <IconBtn onClick={() => removeTag(i)} title="Remove">
                <Ic.trash width="14" height="14"/>
              </IconBtn>
            </div>
          ))}
        </div>
      </Section>
    </Page>
  )
}

function SortableIngredientCard({
  ing, mobile, onPick, onCreateRequest, onView, onRemove, onChange,
}: {
  ing: IngRow
  mobile: boolean
  onPick: (row: IngredientRow) => void
  onCreateRequest: (seed: string) => void
  onView: () => void
  onRemove: () => void
  onChange: (patch: Partial<RecipeIngredient>) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: ing._uid })
  const cat = categoryOf(ing.default_unit)
  const unitOpts = unitsForCategory(cat)
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        display: 'grid', gap: 8, padding: 10,
        border: `1px solid ${theme.lineStrong}`,
        borderRadius: theme.radius,
        background: theme.bg,
        opacity: isDragging ? 0.4 : 1,
        zIndex: isDragging ? 1 : 'auto',
      }}
    >
      <div style={{
        display: 'grid',
        gridTemplateColumns: '16px 1fr auto auto',
        gap: 8, alignItems: 'center', minWidth: 0,
      }}>
        <span
          {...attributes}
          {...listeners}
          title="Drag to reorder"
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            cursor: isDragging ? 'grabbing' : 'grab',
            color: theme.inkSoft, userSelect: 'none',
            // Prevent the browser from hijacking the touch as a scroll gesture
            // — dnd-kit needs the pointer events.
            touchAction: 'none',
          }}
        >
          <Ic.grip width="16" height="16"/>
        </span>
        <div style={{ minWidth: 0 }}>
          <IngredientPicker
            value={(ing.ingredient_id || ing.component_recipe_id) ? { ingredient_id: ing.ingredient_id, name: ing.name, default_unit: ing.default_unit } : null}
            onChange={onPick}
            onCreateRequest={onCreateRequest}
            includeRecipes
          />
        </div>
        {ing.ingredient_id > 0 ? (
          <button onClick={onView} style={btnLink}>View</button>
        ) : (ing.component_recipe_id ?? 0) > 0 ? (
          <span style={{
            fontSize: 10, letterSpacing: 1, textTransform: 'uppercase',
            color: theme.accent, fontWeight: 600,
          }}>recipe</span>
        ) : <span/>}
        <IconBtn onClick={onRemove} title="Remove">
          <Ic.trash width="14" height="14"/>
        </IconBtn>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: mobile ? '1fr 1fr auto' : '90px 90px 1fr auto',
        gap: 8, alignItems: 'center',
      }}>
        <input placeholder="Qty" type="number" step="any" value={ing.quantity || ''}
          onChange={e => onChange({ quantity: Number(e.target.value) || 0 })} style={inputStyle}/>
        <select
          value={ing.unit}
          onChange={e => onChange({ unit: e.target.value })}
          disabled={!ing.ingredient_id && !(ing.component_recipe_id ?? 0)}
          style={{ ...inputStyle, appearance: 'auto' }}
        >
          {!ing.unit && <option value="">—</option>}
          {unitOpts.map(u => <option key={u.code} value={u.code}>{u.label}</option>)}
        </select>
        {mobile && (
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontFamily: theme.sans, fontSize: 12, color: theme.inkSoft,
            whiteSpace: 'nowrap',
          }}>
            <input type="checkbox" checked={ing.optional}
              onChange={e => onChange({ optional: e.target.checked })}/>
            Optional
          </label>
        )}
        <input
          placeholder="prep (e.g. chopped)"
          value={ing.prep ?? ''}
          onChange={e => onChange({ prep: e.target.value })}
          style={{ ...inputStyle, gridColumn: mobile ? '1 / -1' : 'auto' }}
        />
        {!mobile && (
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontFamily: theme.sans, fontSize: 12, color: theme.inkSoft,
          }}>
            <input type="checkbox" checked={ing.optional}
              onChange={e => onChange({ optional: e.target.checked })}/>
            Optional
          </label>
        )}
      </div>
    </div>
  )
}

function Page({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 720 }}>
      {children}
    </div>
  )
}

function Header({ title, onCancel, onSave, saving, disabled }: {
  title: string
  onCancel: () => void
  onSave: () => void
  saving: boolean
  disabled?: boolean
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'space-between' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.4, fontWeight: 600,
          textTransform: 'uppercase', color: theme.muted,
        }}>Editing</div>
        <h1 style={{
          fontFamily: theme.serif, fontSize: 28, fontWeight: theme.headWeight,
          color: theme.ink, margin: '2px 0 0', lineHeight: 1.1,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{title}</h1>
      </div>
      <div style={{ display: 'flex', gap: 8, flex: '0 0 auto' }}>
        <button onClick={onCancel} style={{
          all: 'unset', cursor: 'pointer',
          padding: '8px 14px', borderRadius: theme.radius,
          fontFamily: theme.sans, fontSize: 12, fontWeight: 600, letterSpacing: 0.4,
          textTransform: 'uppercase', color: theme.ink,
          border: `1px solid ${theme.lineStrong}`,
        }}>Cancel</button>
        <button onClick={onSave} disabled={saving || disabled} style={{
          all: 'unset', cursor: saving || disabled ? 'not-allowed' : 'pointer',
          padding: '8px 14px', borderRadius: theme.radius,
          fontFamily: theme.sans, fontSize: 12, fontWeight: 700, letterSpacing: 0.4,
          textTransform: 'uppercase', color: theme.surface,
          background: theme.ink, opacity: saving || disabled ? 0.5 : 1,
        }}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </div>
  )
}

function Section({ label, action, children }: { label: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{
      background: theme.surface, border: theme.cardBorder, borderRadius: theme.radiusLg,
      padding: '14px 16px',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 10,
      }}>
        <div style={{
          fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.4, fontWeight: 700,
          textTransform: 'uppercase', color: theme.muted,
        }}>{label}</div>
        {action}
      </div>
      {children}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{
        fontFamily: theme.sans, fontSize: 10, letterSpacing: 1, fontWeight: 600,
        textTransform: 'uppercase', color: theme.muted,
      }}>{label}</span>
      {children}
    </label>
  )
}

function AddBtn({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button onClick={onClick} style={{
      all: 'unset', cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '4px 10px', borderRadius: theme.radius,
      fontFamily: theme.sans, fontSize: 11, fontWeight: 600, letterSpacing: 0.4,
      textTransform: 'uppercase', color: theme.ink,
      border: `1px solid ${theme.lineStrong}`,
    }}>
      <Ic.plus width="12" height="12"/> {label}
    </button>
  )
}

function IconBtn({ onClick, title, children }: { onClick: () => void; title: string; children: React.ReactNode }) {
  return (
    <button onClick={onClick} title={title} style={{
      all: 'unset', cursor: 'pointer',
      padding: 6, borderRadius: theme.radius, color: theme.inkSoft,
      display: 'inline-flex',
    }}>{children}</button>
  )
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontFamily: theme.sans, fontSize: 12, color: theme.muted, fontStyle: 'italic', marginBottom: 6 }}>
      {children}
    </div>
  )
}

const btnLink: React.CSSProperties = {
  all: 'unset', cursor: 'pointer',
  fontFamily: theme.sans, fontSize: 11, fontWeight: 600, letterSpacing: 0.6,
  textTransform: 'uppercase', color: theme.accentInk,
  padding: '2px 6px', borderRadius: theme.radius, flex: '0 0 auto',
}

const inputStyle: React.CSSProperties = {
  all: 'unset',
  boxSizing: 'border-box',
  width: '100%',
  padding: '7px 10px',
  borderRadius: theme.radius,
  border: `1px solid ${theme.lineStrong}`,
  background: theme.bg,
  fontFamily: theme.sans,
  fontSize: 13,
  color: theme.ink,
}

const pillBtn: React.CSSProperties = {
  all: 'unset',
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '6px 12px', borderRadius: theme.radius,
  fontFamily: theme.sans, fontSize: 11, fontWeight: 600, letterSpacing: 0.4,
  textTransform: 'uppercase',
}

// fileToResizedJpeg downscales an image file in a canvas (longest edge ≤ maxDim)
// and returns base64-encoded JPEG bytes (no data: prefix). The server re-encodes
// authoritatively; this just shrinks the upload and gives an instant result.
async function fileToResizedJpeg(file: File, maxDim = 1024, quality = 0.85): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const fr = new FileReader()
    fr.onload = () => resolve(fr.result as string)
    fr.onerror = () => reject(new Error('could not read file'))
    fr.readAsDataURL(file)
  })
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const im = new Image()
    im.onload = () => resolve(im)
    im.onerror = () => reject(new Error('could not decode image'))
    im.src = dataUrl
  })
  let w = img.naturalWidth || img.width
  let h = img.naturalHeight || img.height
  if (w > maxDim || h > maxDim) {
    if (w >= h) { h = Math.round((h * maxDim) / w); w = maxDim }
    else { w = Math.round((w * maxDim) / h); h = maxDim }
  }
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('no canvas context')
  ctx.drawImage(img, 0, 0, w, h)
  const out = canvas.toDataURL('image/jpeg', quality)
  return out.slice(out.indexOf(',') + 1)
}
